import { ILegalClient } from '../Interfaces/ILegalClient';
import {
  ClientWarning,
  classifyClientReachability,
  fullName,
  joinNames,
  portalBlockReason,
} from '../helper/clientEmailStatus';

/**
 * Ein Mandant ist "zum Empfang ausgewählt", wenn für ihn eine Rechnungs- oder
 * Urkunden-Erinnerung aktiviert ist. Nur solche Mandanten werden gewarnt.
 */
const isSelectedForReminding = (client: ILegalClient) =>
  !!client.enableInvoiceReminding || !!client.enableCertificateReminding;

/** Die Mandanten, die für die Erreichbarkeits-Warnungen überhaupt zählen. */
export const getClientsRelevantForReception = (clients: ILegalClient[]) =>
  clients.filter(isSelectedForReminding);

/**
 * Fall 1 (rot): keine E-Mail-Adresse hinterlegt. Weder Portal-Login noch
 * E-Mail-Benachrichtigung möglich — der schwerwiegendste Fall.
 */
export const buildNoEmailText = (noEmail: ILegalClient[]) =>
  noEmail.length > 1
    ? `Für ${joinNames(noEmail)} sind keine E-Mail-Adressen hinterlegt. Diese Mandanten können sich daher nicht im Mandantenportal anmelden, erhalten keine Benachrichtigungen und haben keinen Zugriff auf bereitgestellte Dokumente. Bitte hinterlegen Sie eine E-Mail-Adresse oder deaktivieren Sie die Erinnerungen.`
    : `Für ${joinNames(noEmail)} ist keine E-Mail-Adresse hinterlegt. Der Mandant kann sich daher nicht im Mandantenportal anmelden, erhält keine Benachrichtigungen und hat keinen Zugriff auf bereitgestellte Dokumente. Bitte hinterlegen Sie eine E-Mail-Adresse oder deaktivieren Sie die Erinnerungen.`;

/**
 * Fall 3 (rot): E-Mail vorhanden, aber kein Portalzugang. Dokumente können
 * nicht geöffnet werden; der Grund ist aus den Daten ableitbar.
 */
export const buildNoPortalText = (noPortal: ILegalClient[]) => {
  const entries = noPortal
    .map(client => `${fullName(client)} (${portalBlockReason(client)})`)
    .join(', ');

  return noPortal.length > 1
    ? `Die folgenden Mandanten können sich derzeit nicht im Mandantenportal anmelden und haben daher keinen Zugriff auf bereitgestellte Dokumente: ${entries}. Bitte prüfen Sie den Zugang zum Mandantenportal.`
    : `${entries} kann sich derzeit nicht im Mandantenportal anmelden und hat daher keinen Zugriff auf bereitgestellte Dokumente. Bitte prüfen Sie den Zugang zum Mandantenportal.`;
};

/**
 * Fall 2 (orange): E-Mail vorhanden, Portalzugang möglich, aber E-Mail-Empfang
 * deaktiviert — nur keine Benachrichtigung.
 */
export const buildNoNotificationText = (noNotification: ILegalClient[]) =>
  noNotification.length > 1
    ? `${joinNames(noNotification)} können Dokumente im Mandantenportal abrufen, erhalten jedoch keine Benachrichtigungen per E-Mail. Bitte aktivieren Sie den Empfang von E-Mails.`
    : `${joinNames(noNotification)} kann Dokumente im Mandantenportal abrufen, erhält jedoch keine Benachrichtigungen per E-Mail. Bitte aktivieren Sie den Empfang von E-Mails.`;

/**
 * Ermittelt die Warnungen für die Mandanten-Übersicht eines Vorgangs (#1198).
 * Betrachtet nur Mandanten mit aktiver Erinnerung (Rechnung/Urkunden) und
 * unterscheidet drei sich gegenseitig ausschließende Zustände (siehe die
 * Textbausteine oben).
 *
 * Seit #1283 speisen dieselben Bausteine den Health Check, dort aber als drei
 * einzeln abschaltbare Regeln — deshalb sind die Texte oben getrennt exportiert
 * und diese Funktion nur noch deren Komposition.
 *
 * @param clients die dem Vorgang zugeordneten Mandanten
 */
export const getClientReceptionWarnings = (
  clients: ILegalClient[]
): ClientWarning[] => {
  const { noEmail, noPortal, noNotification } = classifyClientReachability(
    getClientsRelevantForReception(clients)
  );

  const warnings: ClientWarning[] = [];

  if (noEmail.length > 0) {
    warnings.push({ severity: 'error', text: buildNoEmailText(noEmail) });
  }

  if (noPortal.length > 0) {
    warnings.push({ severity: 'error', text: buildNoPortalText(noPortal) });
  }

  if (noNotification.length > 0) {
    warnings.push({
      severity: 'warning',
      text: buildNoNotificationText(noNotification),
    });
  }

  return warnings;
};
